import { getWoodPattern, roundedRect } from '@bn-games/render-helpers';
import type { DominoPlayer, Tile } from '../game/types.js';
import {
  BOARD_BORDER_THICKNESS,
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  HAND_ZONE_THICKNESS,
  RACK_GAP,
  RACK_TILE_SCALE,
  STACK_OFFSET,
  TILE_CORNER_RADIUS,
  TILE_LONG,
  TILE_SHORT,
} from '../utils/constants.js';
import type { RenderPalette } from './palette.js';
import { drawTileBack, drawTileFace, rectBounds, type TileRect } from './TileRenderer.js';

/**
 * A hand zone reserves a rectangular region of the canvas for one seat's
 * tiles. The seat's screen position determines which edge of the table the
 * zone hugs (south = bottom, west = left, north = top, east = right) and
 * therefore which axis the tile rack runs along + how face-down opponent
 * stacks are oriented.
 */
export interface HandZone {
  readonly seat: DominoPlayer;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface RackTilePlacement {
  readonly rect: TileRect;
  readonly tile: Tile;
  readonly index: number;
}

/**
 * Places the local player's tiles in their hand zone. South (bottom of
 * table) lays out horizontally with tile long axis vertical so the tile
 * stands "upright" facing the user.
 */
export function layoutLocalRack(zone: HandZone, hand: readonly Tile[]): RackTilePlacement[] {
  if (hand.length === 0) return [];
  const orientation: TileRect['orientation'] = orientationForSeat(zone.seat);
  const rackLong = TILE_LONG * RACK_TILE_SCALE;
  const rackShort = TILE_SHORT * RACK_TILE_SCALE;
  const tileWidth = orientation === 'vertical' ? rackShort : rackLong;
  const tileHeight = orientation === 'vertical' ? rackLong : rackShort;

  const isHorizontalRow = zone.seat === 'south' || zone.seat === 'north';
  const placements: RackTilePlacement[] = [];

  if (isHorizontalRow) {
    const totalWidth = hand.length * tileWidth + (hand.length - 1) * RACK_GAP;
    const startX = zone.x + (zone.width - totalWidth) / 2;
    const y = zone.y + (zone.height - tileHeight) / 2;
    for (let i = 0; i < hand.length; i += 1) {
      const x = startX + i * (tileWidth + RACK_GAP);
      placements.push({
        rect: { x, y, long: rackLong, short: rackShort, orientation },
        tile: hand[i]!,
        index: i,
      });
    }
  } else {
    const totalHeight = hand.length * tileHeight + (hand.length - 1) * RACK_GAP;
    const startY = zone.y + (zone.height - totalHeight) / 2;
    const x = zone.x + (zone.width - tileWidth) / 2;
    for (let i = 0; i < hand.length; i += 1) {
      const y = startY + i * (tileHeight + RACK_GAP);
      placements.push({
        rect: { x, y, long: rackLong, short: rackShort, orientation },
        tile: hand[i]!,
        index: i,
      });
    }
  }
  return placements;
}

/** Draw the local player's face-up rack, optionally marking one tile selected. */
export function drawLocalRack(
  ctx: CanvasRenderingContext2D,
  placements: readonly RackTilePlacement[],
  palette: RenderPalette,
  options: {
    selectedIndex?: number;
    hoverIndex?: number;
    /** Index of a tile currently being dragged — drawn as an empty slot. */
    draggingIndex?: number;
    /**
     * Index of the tile the player is FORCED to play on this turn (Телефон
     * lead). Drawn with a pulsing ring so the player can see at a glance
     * which tile to drag.
     */
    forcedIndex?: number;
  } = {},
): void {
  for (const p of placements) {
    if (options.draggingIndex === p.index) {
      drawEmptyRackSlot(ctx, p.rect, palette);
      continue;
    }
    const isForced = options.forcedIndex === p.index;
    drawTileFace(ctx, p.rect, p.tile[0], p.tile[1], palette, {
      // Reuse the existing selected-ring treatment to mark the forced tile.
      selected: isForced || options.selectedIndex === p.index,
      hover: options.hoverIndex === p.index,
    });
    if (isForced) drawForcedTileBadge(ctx, p.rect, palette);
  }
}

/** Outer glow around the forced tile so it's unmissable in the rack. */
function drawForcedTileBadge(
  ctx: CanvasRenderingContext2D,
  rect: TileRect,
  palette: RenderPalette,
): void {
  const { x, y, width, height } = rectBounds(rect);
  ctx.save();
  ctx.shadowColor = palette.validArmGlow;
  ctx.shadowBlur = 18;
  ctx.strokeStyle = palette.validArmGlow;
  ctx.lineWidth = 2;
  roundedRect(ctx, x - 2.5, y - 2.5, width + 5, height + 5, TILE_CORNER_RADIUS + 3);
  ctx.stroke();
  ctx.restore();
}

/** Soft outlined placeholder shown where a dragged tile used to sit. */
function drawEmptyRackSlot(
  ctx: CanvasRenderingContext2D,
  rect: TileRect,
  palette: RenderPalette,
): void {
  const { x, y, width, height } = rectBounds(rect);
  ctx.save();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
  roundedRect(ctx, x, y, width, height, TILE_CORNER_RADIUS);
  ctx.fill();
  ctx.strokeStyle = palette.handZoneBorder;
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 4]);
  roundedRect(ctx, x + 0.5, y + 0.5, width - 1, height - 1, TILE_CORNER_RADIUS);
  ctx.stroke();
  ctx.restore();
}

/**
 * Opponent hand — drawn as a face-down stack. Tile count lives in the
 * per-seat PlayerInfo card, not on top of the stack itself.
 * Stacks fan along the seat's hand-rack axis with each tile offset by
 * `STACK_OFFSET` so the bottom of the pile is visible.
 */
export function drawOpponentStack(
  ctx: CanvasRenderingContext2D,
  zone: HandZone,
  tileCount: number,
  palette: RenderPalette,
): void {
  if (tileCount <= 0) return;
  const orientation: TileRect['orientation'] = orientationForSeat(zone.seat);
  const rackLong = TILE_LONG * RACK_TILE_SCALE;
  const rackShort = TILE_SHORT * RACK_TILE_SCALE;
  const tileWidth = orientation === 'vertical' ? rackShort : rackLong;
  const tileHeight = orientation === 'vertical' ? rackLong : rackShort;

  const isHorizontalRow = zone.seat === 'south' || zone.seat === 'north';
  const majorTile = isHorizontalRow ? tileWidth : tileHeight;
  const availableMajor =
    (isHorizontalRow ? zone.width : zone.height) - OPPONENT_STACK_END_PADDING * 2;

  const layout = layoutOpponentStrip(majorTile, tileCount, availableMajor);
  const totalUsed = (layout.visibleCount - 1) * layout.stride + majorTile;

  if (isHorizontalRow) {
    const startX = zone.x + (zone.width - totalUsed) / 2;
    const y = zone.y + (zone.height - tileHeight) / 2;
    for (let i = 0; i < layout.visibleCount; i += 1) {
      drawTileBack(
        ctx,
        {
          x: startX + i * layout.stride,
          y,
          long: rackLong,
          short: rackShort,
          orientation,
        },
        palette,
      );
    }
  } else {
    const startY = zone.y + (zone.height - totalUsed) / 2;
    const x = zone.x + (zone.width - tileWidth) / 2;
    for (let i = 0; i < layout.visibleCount; i += 1) {
      drawTileBack(
        ctx,
        {
          x,
          y: startY + i * layout.stride,
          long: rackLong,
          short: rackShort,
          orientation,
        },
        palette,
      );
    }
  }
}

/**
 * Pixel reserve on each end of an opponent's row/column so tiles don't
 * touch the tray hairline (and the count badge has somewhere to live at
 * the inner corner).
 */
const OPPONENT_STACK_END_PADDING = 8;

/**
 * Compute (stride, visibleCount) for an opponent's tile strip along the
 * rack's major axis. The intent: spread tiles with normal `RACK_GAP` if
 * there's room, otherwise spread them evenly across the available space,
 * never tighter than `STACK_OFFSET`. Falls back to clipping visible count
 * when even the minimum stride overflows the tray.
 */
function layoutOpponentStrip(
  majorTile: number,
  tileCount: number,
  availableMajor: number,
): { stride: number; visibleCount: number } {
  if (tileCount <= 1) return { stride: 0, visibleCount: 1 };

  const spreadStride = majorTile + RACK_GAP;
  const spreadTotal = (tileCount - 1) * spreadStride + majorTile;
  if (spreadTotal <= availableMajor) {
    return { stride: spreadStride, visibleCount: tileCount };
  }

  const fitStride = (availableMajor - majorTile) / (tileCount - 1);
  if (fitStride >= STACK_OFFSET) {
    return { stride: fitStride, visibleCount: tileCount };
  }

  const stride = STACK_OFFSET;
  const visibleCount = Math.max(
    1,
    Math.min(tileCount, Math.floor((availableMajor - majorTile) / stride) + 1),
  );
  return { stride, visibleCount };
}

/**
 * Vertical seam separating two sub-regions inside a row hand zone (north or
 * south) at the inner edge of an adjacent west/east column. Dark hairline +
 * highlight reads as carved into the tray surface.
 */
function drawHandZoneSeam(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  height: number,
): void {
  const seamX = Math.round(x);
  ctx.save();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.fillRect(seamX - 1, y, 1, height);
  ctx.fillStyle = 'rgba(255, 220, 160, 0.14)';
  ctx.fillRect(seamX, y, 1, height);
  ctx.restore();
}

/**
 * In 3p/4p layouts the north + south rows span the full inner width, so
 * each row owns the corner column above/below an adjacent side tray. This
 * pass adds a vertical seam at the boundary of each side column so the
 * corner subregion reads as a distinct compartment from the main rack.
 */
export function drawHandZoneDividers(
  ctx: CanvasRenderingContext2D,
  seatZones: Readonly<Record<DominoPlayer, HandZone | null>>,
): void {
  const { north, south, west, east } = seatZones;
  if (west) {
    const seamX = west.x + west.width;
    if (north) drawHandZoneSeam(ctx, seamX, north.y, north.height);
    if (south) drawHandZoneSeam(ctx, seamX, south.y, south.height);
  }
  if (east) {
    const seamX = east.x;
    if (north) drawHandZoneSeam(ctx, seamX, north.y, north.height);
    if (south) drawHandZoneSeam(ctx, seamX, south.y, south.height);
  }
}

/**
 * Recessed wood-tray frame for a seat's hand zone. Mirrors the backgammon
 * bear-off tray treatment: wood pattern + dark overlay (depth) + inset
 * shadow on top/left + slight glow on bottom/right + hairline outline.
 */
export function drawHandZoneFrame(
  ctx: CanvasRenderingContext2D,
  zone: HandZone,
  palette: RenderPalette,
): void {
  const { x, y, width: w, height: h } = zone;
  ctx.save();

  // Wood floor — same base pattern as the chain area, but rotated 90° so
  // the grain runs perpendicular and the trays read as a separate piece of
  // wood. Reset the pattern transform afterwards so the chain area on the
  // next frame still paints with the default orientation.
  const pattern = getWoodPattern(ctx, palette.wood, {
    width: CANVAS_WIDTH,
    height: CANVAS_HEIGHT,
  });
  pattern.setTransform(new DOMMatrix().rotate(90));
  ctx.fillStyle = pattern;
  ctx.fillRect(x, y, w, h);
  pattern.setTransform(new DOMMatrix());

  // Soft darkening — trays read as shallow pockets in the shelf, not the
  // deeper cavity that the chain region sits in.
  ctx.fillStyle = 'rgba(0, 0, 0, 0.20)';
  ctx.fillRect(x, y, w, h);

  // Inset shadow — top + left dark (light from upper-left).
  ctx.fillStyle = 'rgba(0, 0, 0, 0.40)';
  ctx.fillRect(x, y, w, 1);
  ctx.fillRect(x, y, 1, h);
  // Bottom + right slight glow.
  ctx.fillStyle = 'rgba(255, 220, 160, 0.12)';
  ctx.fillRect(x, y + h - 1, w, 1);
  ctx.fillRect(x + w - 1, y, 1, h);

  // Hairline outline.
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);

  ctx.restore();
}

/**
 * Three-line label showing one seat's name + score + tile count. Each seat
 * owns one of the four corner cells of the board (south → bottom-left,
 * west → top-left, north → top-right, east → bottom-right). The text sits
 * at the top-left of that cell with no card chrome — the wood tray reads
 * through. The active seat's name lights up (gold + glow) so it's easy to
 * track whose turn it is at a glance.
 */
export interface PlayerInfo {
  readonly name: string;
  readonly score: number;
  readonly tileCount: number;
  readonly isActive: boolean;
  readonly scoreLabel: string;
  readonly tilesLabel: string;
}

const PLAYER_INFO_PAD_X = 12;
const PLAYER_INFO_PAD_Y = 10;
const PLAYER_INFO_W = 110;

/**
 * Top-left of the text block for a seat's player info. Each block anchors
 * to a (HAND_ZONE_THICKNESS × HAND_ZONE_THICKNESS) corner cell of the
 * playfield, mapped table-clockwise from the local viewer (south).
 */
function playerInfoOrigin(seat: DominoPlayer): { x: number; y: number } {
  const cell = HAND_ZONE_THICKNESS;
  const innerLeft = BOARD_BORDER_THICKNESS;
  const innerRight = CANVAS_WIDTH - BOARD_BORDER_THICKNESS;
  const innerTop = BOARD_BORDER_THICKNESS;
  const innerBottom = CANVAS_HEIGHT - BOARD_BORDER_THICKNESS;
  const leftX = innerLeft + PLAYER_INFO_PAD_X;
  const rightX = innerRight - cell + PLAYER_INFO_PAD_X;
  const topY = innerTop + PLAYER_INFO_PAD_Y;
  const bottomY = innerBottom - cell + PLAYER_INFO_PAD_Y;
  switch (seat) {
    case 'south':
      return { x: leftX, y: bottomY };
    case 'west':
      return { x: leftX, y: topY };
    case 'north':
      return { x: rightX, y: topY };
    case 'east':
      return { x: rightX, y: bottomY };
  }
}

export function drawPlayerInfo(
  ctx: CanvasRenderingContext2D,
  seat: DominoPlayer,
  info: PlayerInfo,
  palette: RenderPalette,
): void {
  const { x, y } = playerInfoOrigin(seat);
  const maxNameWidth = PLAYER_INFO_W;
  ctx.save();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  let cursorY = y;
  ctx.font = "700 14px 'Segoe UI', system-ui, sans-serif";
  if (info.isActive) {
    ctx.shadowColor = palette.validArmGlow;
    ctx.shadowBlur = 8;
    ctx.fillStyle = palette.validArmGlow;
  } else {
    ctx.fillStyle = palette.textPrimary;
  }
  ctx.fillText(truncate(ctx, info.name, maxNameWidth), x, cursorY);
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  cursorY += 18;

  ctx.font = "500 12px 'Segoe UI', system-ui, sans-serif";
  ctx.fillStyle = palette.textSecondary;
  ctx.fillText(`${info.scoreLabel}: ${info.score}`, x, cursorY);
  cursorY += 15;
  ctx.fillText(`${info.tilesLabel}: ${info.tileCount}`, x, cursorY);

  ctx.restore();
}

function truncate(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  const ellipsis = '…';
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(text.slice(0, mid) + ellipsis).width <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(0, lo) + ellipsis;
}

function orientationForSeat(seat: DominoPlayer): TileRect['orientation'] {
  // South/north racks face the local user along the horizontal axis — tile
  // long axis stands vertical. West/east stacks line along the vertical
  // axis — tile long axis lies horizontal.
  switch (seat) {
    case 'south':
    case 'north':
      return 'vertical';
    case 'west':
    case 'east':
      return 'horizontal';
  }
}
